import { CheckOutlined, CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Checkbox, Modal, PopConfirm, Switch, Table, useAsync } from '@maxtropy/components';
import { Space } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { columns } from './util';
import SelectRuleModal from './SelectRuleModal';
import styles from './index.module.scss';
import SelectDeviceModal from './SelectDeviceModal';
import { getStaffChannelApp } from '@/shared/api/common';
import { ChannelApp, PushThirdApp } from '@/shared/types';
import { apiV2CommonPushSendEmailStatusPost } from '@maxtropy/device-customer-apis-v2';
import { cloneDeep } from 'lodash-es';

export interface ISelectAlarmRules {
  value?: any[];
  onChange?: (value: any[]) => void;
  pushConfig?: string;
  disabled?: boolean;
  pushCenterStaffLinkVoListForm?: any[];
  outsidersForm?: any[];
}

const SelectAlarmRules: FC<ISelectAlarmRules> = props => {
  const { value, onChange, pushConfig, disabled = false, pushCenterStaffLinkVoListForm, outsidersForm } = props;
  const [visible, setVisible] = useState<boolean>(false); // 选择报警规则
  const [deviceVisible, setDeviceVisible] = useState<boolean>(false); // 选择监控设备
  const staffChannelApps = useAsync(getStaffChannelApp); // 查询推送渠道

  const [dataSource, setDataSource] = useState<any[]>([]);
  // 选择设备
  const [selectRecord, setSelectRecord] = useState<any>();
  const [deviceSeletedIds, setDeviceSeletedIds] = useState<number[]>([]);

  const [allUserEmails, setAllUserEmails] = useState<boolean>(false);
  // 判断内外部人员是否都绑定了邮箱
  useEffect(() => {
    const insideStaff = (pushCenterStaffLinkVoListForm ?? []).map(i => i?.email);
    const outersideStaff = (outsidersForm ?? []).map(i => i?.email);
    const addArr = [...insideStaff, ...outersideStaff];
    const isAllsetEmail = addArr.every(item => item !== undefined && item !== null && item !== '');
    setAllUserEmails(isAllsetEmail);
  }, [pushCenterStaffLinkVoListForm, outsidersForm]);

  const unBind = (record: any) => {
    Modal.confirm({
      title: '取消绑定？',
      okText: '继续',
      onOk() {
        const newValue = (value ?? []).filter(i => i.ruleId !== record.ruleId);
        onChange?.(newValue);
      },
    });
  };

  const changeRecordThirdApp = (checked: boolean, channelApp: ChannelApp, record: any) => {
    const index = (value ?? []).findIndex(i => i.ruleId === record.ruleId);
    const newRecord = cloneDeep(record);
    if (index !== -1) {
      const thirdAppIndex = newRecord.thirdPartyAppId?.findIndex((i: any) => i === channelApp.channelId);
      if (checked) {
        if (thirdAppIndex === undefined || thirdAppIndex === -1) {
          newRecord.thirdPartyAppId = [...newRecord.thirdPartyAppId, channelApp.channelId];
        }
      } else {
        if (thirdAppIndex !== -1) {
          newRecord.thirdPartyAppId = record.thirdPartyAppId.filter((i: any) => i !== channelApp.channelId);
        }
      }
      const newValue = [...(value ?? []).slice(0, index), newRecord, ...(value ?? []).slice(index + 1)];
      onChange?.(newValue);
    }
  };
  const changeResumeRecordThirdApp = (checked: boolean, channelApp: ChannelApp, record: any) => {
    const index = (value ?? []).findIndex(i => i.ruleId === record.ruleId);
    const newRecord = cloneDeep(record);
    if (index !== -1) {
      const thirdAppIndex = newRecord.resumeAlarmIdList?.findIndex((i: any) => i === channelApp.channelId);
      if (checked) {
        if (thirdAppIndex === undefined || thirdAppIndex === -1) {
          newRecord.resumeAlarmIdList = [...newRecord.resumeAlarmIdList, channelApp.channelId];
        }
      } else {
        if (thirdAppIndex !== -1) {
          newRecord.resumeAlarmIdList = record.resumeAlarmIdList?.filter((i: any) => i !== channelApp.channelId);
        }
      }
      const newValue = [...(value ?? []).slice(0, index), newRecord, ...(value ?? []).slice(index + 1)];
      onChange?.(newValue);
    }
  };

  // 点击表头复选框：把所有行设置为 checked（true/false）
  const toggleAll = (e: { target: { checked: any } }, channelApp: ChannelApp) => {
    const checked = e.target.checked;
    const newValue = cloneDeep(value);
    let finalValue: any[] = [];
    if (checked) {
      finalValue = (newValue ?? []).map(i => ({
        ...i,
        thirdPartyAppId: [...(i.thirdPartyAppId || []), channelApp.channelId],
      }));
    } else {
      finalValue = (newValue ?? []).map(item => ({
        // 过滤掉指定的值
        ...item,
        thirdPartyAppId: item.thirdPartyAppId.filter((v: any) => v !== channelApp.channelId),
      }));
    }
    onChange?.(finalValue);
  };

  // 点击表头复选框：把所有行设置为 checked（true/false）
  const toggleAllResume = (e: { target: { checked: any } }, channelApp: ChannelApp) => {
    const checked = e.target.checked;
    const newValue = cloneDeep(value);
    let finalValue: any[] = [];
    if (checked) {
      finalValue = (newValue ?? []).map(i => ({
        ...i,
        resumeAlarmIdList: [...(i.resumeAlarmIdList || []), channelApp.channelId],
      }));
    } else {
      finalValue = (newValue ?? []).map(item => ({
        // 过滤掉指定的值
        ...item,
        resumeAlarmIdList: item.resumeAlarmIdList.filter((v: any) => v !== channelApp.channelId),
      }));
    }
    onChange?.(finalValue);
  };

  // 计算 header checkbox 的状态
  const allChecked = useCallback(
    (channel: ChannelApp) => {
      return (value ?? []).length > 0 && (value ?? []).every(r => r?.thirdPartyAppId?.includes(channel?.channelId));
    },
    [value]
  );

  const someChecked = useCallback(
    (channel: ChannelApp) => {
      return (value ?? []).length > 0 && (value ?? []).some(r => r?.thirdPartyAppId?.includes(channel?.channelId));
    },
    [value]
  );

  const allResumeChecked = useCallback(
    (channel: ChannelApp) => {
      return (value ?? []).length > 0 && (value ?? []).every(r => r?.resumeAlarmIdList?.includes(channel?.channelId));
    },
    [value]
  );

  const someResumeChecked = useCallback(
    (channel: ChannelApp) => {
      return (value ?? []).length > 0 && (value ?? []).some(r => r?.resumeAlarmIdList?.includes(channel?.channelId));
    },
    [value]
  );

  const staffChannelColumns = useMemo(() => {
    if (pushConfig === 'rule' && staffChannelApps) {
      return [
        ...(staffChannelApps ?? []).map(i => ({
          title: (
            <div className={styles.custom_title}>
              <div className={styles.channel_title}>
                {i.channelId !== PushThirdApp.Email && !disabled && (
                  <Checkbox
                    checked={allChecked(i)}
                    indeterminate={someChecked(i) && !allChecked(i)}
                    onChange={e => toggleAll(e, i)}
                  />
                )}
                {i.channelName}
              </div>
              {i.channelId !== PushThirdApp.WEB_SOCKET && (
                <div className={styles.channel_title}>
                  {i.channelId !== PushThirdApp.Email && !disabled && (
                    <Checkbox
                      checked={allResumeChecked(i)}
                      indeterminate={someResumeChecked(i) && !allResumeChecked(i)}
                      onChange={e => toggleAllResume(e, i)}
                    />
                  )}
                  恢复通知
                </div>
              )}
            </div>
          ),
          width: 200,
          render: (_: undefined, record: any) => {
            const checked = !!record?.thirdPartyAppId?.find((j: any) => j === i?.channelId);
            const isResumeAlarm = !!record?.resumeAlarmIdList?.find((j: any) => j === i?.channelId);
            let element = (
              <div className={styles.custom_column}>
                <div style={{ width: 68 }}>{checked ? '开' : '关'}</div>
                {i.channelId !== PushThirdApp.WEB_SOCKET && (
                  <div style={{ width: 68 }}>{isResumeAlarm ? '开' : '关'}</div>
                )}
              </div>
            );
            if (!disabled) {
              element = (
                <div className={styles.custom_column}>
                  <div style={{ width: i.channelId === PushThirdApp.Email ? 48 : 68 }}>
                    <Switch
                      checkedChildren={<CheckOutlined />}
                      unCheckedChildren={<CloseOutlined />}
                      checked={checked}
                      onChange={async (checked: boolean) => {
                        if (i.channelId === PushThirdApp.Email) {
                          // 先判断该人员是否配置邮箱
                          if (!allUserEmails) {
                            Modal.error({
                              title: '用户邮箱未配置,邮件无法发送',
                              okText: '确定',
                            });
                            return;
                          }
                          // 再判断该租户是否配置了邮件服务
                          const res = await apiV2CommonPushSendEmailStatusPost({});
                          if (!res?.ret) return;
                        }

                        changeRecordThirdApp(checked, i, record);
                      }}
                    />
                  </div>
                  {i.channelId !== PushThirdApp.WEB_SOCKET && (
                    <div style={{ width: 68 }}>
                      <Switch
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        checked={isResumeAlarm}
                        onChange={async (checked: boolean) => {
                          if (i.channelId === PushThirdApp.Email) {
                            // 先判断该人员是否配置邮箱
                            if (!allUserEmails) {
                              Modal.error({
                                title: '用户邮箱未配置,邮件无法发送',
                                okText: '确定',
                              });
                              return;
                            }
                            // 再判断该租户是否配置了邮件服务
                            const res = await apiV2CommonPushSendEmailStatusPost({});
                            if (!res?.ret) return;
                          }

                          changeResumeRecordThirdApp(checked, i, record);
                        }}
                      />
                    </div>
                  )}
                </div>
              );
            }

            return element;
          },
        })),
      ];
    }
    return [];
  }, [pushConfig, staffChannelApps, value, allUserEmails]);

  const buildColumns = useMemo(() => {
    return [
      ...columns,
      ...staffChannelColumns,
      {
        title: '操作',
        dataIndex: 'operation',
        width: 180,
        fixed: 'right' as const,
        render: (_: any, record: any) => {
          return (
            <Space size={8}>
              <Button
                type="link"
                onClick={() => {
                  unBind(record);
                }}
              >
                取消绑定
              </Button>
              <Button
                type="link"
                onClick={() => {
                  setDeviceVisible(true);
                  setSelectRecord(record);
                }}
              >
                选择监控设备
              </Button>
            </Space>
          );
        },
      },
    ];
  }, [staffChannelColumns]);

  const disabledIds = useMemo(() => {
    return (value ?? []).map(i => i.ruleId);
  }, [value]);

  // 同步value与dataSource信息
  useEffect(() => {
    setDataSource(
      (value ?? []).map(i => ({
        ...i,
        name: i.ruleName,
      }))
    );
  }, [value]);

  // 取消选择，则使用form上的value
  const onCancel = () => {
    setDataSource(
      (value ?? []).map(i => ({
        ...i,
        name: i.ruleName,
      }))
    );
    setVisible(false);
  };

  // 调用onchange改变form的值
  const onOk = () => {
    onChange?.(
      dataSource
        .filter(i => i?.ruleId)
        .map(i => ({
          ...i,
          ruleName: i?.ruleName ?? i?.name,
        }))
    );
    setVisible(false);
  };

  // 取消设备绑定
  const onDeviceCancel = () => {
    setDeviceVisible(false);
    setDeviceSeletedIds([]);
  };

  // 设备选择
  const onDeviceOk = () => {
    const newData = [...dataSource];
    const currentRowIndex = newData.findIndex(i => i.ruleId === selectRecord.ruleId);
    newData[currentRowIndex].deviceIdList = deviceSeletedIds;
    onChange?.(newData);
    setDeviceVisible(false);
    setDeviceSeletedIds([]);
  };

  // 全部移除
  const onDeleteAll = () => {
    onChange?.([]);
  };

  return (
    <>
      <div className={styles.action}>
        {!disabled && (
          <>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setVisible(true)}>
              选择报警规则
            </Button>
            <PopConfirm title="你确定要移除全部报警规则吗？" onConfirm={onDeleteAll}>
              <Button disabled={value?.length === 0}>全部移除</Button>
            </PopConfirm>
          </>
        )}
      </div>

      <Table
        sticky
        scroll={{ x: 1300, y: 400 }}
        columns={disabled ? [...columns, ...staffChannelColumns] : buildColumns}
        rowKey="ruleId"
        dataSource={value ?? []}
      />
      <Modal
        className={styles.modal}
        title="规则选择"
        destroyOnClose
        open={visible}
        onOk={onOk}
        onCancel={onCancel}
        size="big"
      >
        <SelectRuleModal onChange={setDataSource} value={dataSource} disabledIds={disabledIds} />
      </Modal>
      <Modal
        className={styles.modal}
        title="设备选择"
        destroyOnClose
        open={deviceVisible}
        onOk={onDeviceOk}
        onCancel={onDeviceCancel}
        size="big"
      >
        <SelectDeviceModal
          deviceVisible={deviceVisible}
          selectRecord={selectRecord}
          deviceSeletedIds={deviceSeletedIds}
          setDeviceSeletedIds={setDeviceSeletedIds}
        />
      </Modal>
    </>
  );
};

export default SelectAlarmRules;
